import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kTimelineApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { isEditingLeaseDenial, EditingLeaseErrorDetailSchema, EditingLeaseReadSchema } from '@/editing-leases/schemas'
import { getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** Each operation uses the Timeline lifecycle contract and authenticated identity. */
export async function requestEditingLease(matterId: string, operation: 'status' | 'acquire' | 'renew', activityAgeSeconds?: number) {
  const sentry_transaction_id = nanoid()
  const url = `${kPaxtonAppApiRESTBaseUrl()}${kTimelineApiBasePath}/editing-lease${operation === 'status' ? `?matter_id=${matterId}` : `/${operation}`}`
  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error('Authentication required')
    const response = await fetch(url, {
      method: operation === 'status' ? 'GET' : 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'X-Transaction-Id': sentry_transaction_id },
      ...(operation === 'status' ? {} : { body: JSON.stringify({ matter_id: matterId, activity_age_seconds: activityAgeSeconds }) }),
    })
    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const parsed = EditingLeaseErrorDetailSchema.safeParse(data && typeof data === 'object' && 'detail' in data ? data.detail : null)
      if (!isEditingLeaseDenial(response.status, parsed)) {
        Sentry.captureException(new Error(`Editing lease request failed: ${response.status}`), {
          tags: { transaction_id: sentry_transaction_id },
          extra: getResponseDebugContext(response, url),
        })
      }
      throw new ApiError(response.status, 'Could not update editing availability', parsed.success ? parsed.data : undefined)
    }
    const parsed = EditingLeaseReadSchema.safeParse(data)
    if (parseError || !parsed.success) {
      Sentry.captureException(new Error('Invalid editing lease response'), {
        tags: { transaction_id: sentry_transaction_id },
        extra: { ...getResponseDebugContext(response, url), responseKeys: getTopLevelKeys(data) },
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid editing lease response')
    }
    if (parsed.data.resource.matter_id !== matterId || parsed.data.resource.resource_id !== matterId) {
      Sentry.captureException(new Error('Editing lease resource mismatch'), {
        tags: { transaction_id: sentry_transaction_id },
        extra: getResponseDebugContext(response, url),
      })
      throw new ApiResponsePayloadError(response.status, 'Editing lease resource mismatch')
    }
    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.captureException(error, {
        tags: { transaction_id: sentry_transaction_id },
        extra: { requestUrl: url, onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
      })
    }
    throw error
  }
}
